import { FC, useMemo } from "react";
import { FieldFormV2, FieldV2, LoadingV2 } from "@ucl/library";
import { FIELD_KEYS, CUSTOMER_SEGMENT } from "@/constants";
import "./PersonalAccountAcceptableUse.scss";
import { FinancialApplication } from "@/types/FinancialApplication";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { PersonalAccountAcceptableUseComplexValue } from "../ComplexFields/PersonalAccountAcceptableUseComplexField";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { useFinancialProducts } from "@/hooks/useFinancialProducts";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import {
  useFinancialApplicationFormSectionValidation,
  FinancialApplicationFormValidationDisplayBehavior,
  FinancialApplicationFormSectionContextType,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { FinancialApplicationSection } from "@/types/enums";

export interface PersonalAccountAcceptableUseFormProps {
  financialApplicationId: string;
  autoValidationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  validationContextType: FinancialApplicationFormSectionContextType;
}

export const PersonalAccountAcceptableUseForm: FC<PersonalAccountAcceptableUseFormProps> = (
  props
) => {
  const apiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { coreData, coreDataLoading } = useFinancialApplicationCoreData(
    props.financialApplicationId
  );
  const { products, loading: productsLoading } = useFinancialProducts(apiClient);

  // Get active personal products from deposit applications
  const personalProducts = useMemo(() => {
    if (!coreData?.depositApplications || !products.length) {
      return [];
    }

    return coreData.depositApplications
      .filter((depositApp) => depositApp.isActive)
      .map((depositApp) => {
        const product = products.find((p) => p.id === depositApp.financialProductId);
        return product;
      })
      .filter((product) => product && product.customerSegment === CUSTOMER_SEGMENT.PERSONAL)
      .map((product) => ({
        id: product!.productSlug,
        title: product!.productName,
        description: product!.productDescription,
        features: product!.productFeatures,
        customerSegment: product!.customerSegment,
      }));
  }, [coreData?.depositApplications, products]);

  const { formValue, setFormValue, saveFormData, schemaFactory, formBuilder, fieldFormModule } =
    useFinancialApplicationForm(props.financialApplicationId, props.validationContextType);

  const { validationErrors } =
    useFinancialApplicationFormSectionValidation(
      props.financialApplicationId,
      FinancialApplicationSection.PersonalAccountAcceptableUse,
      props.autoValidationBehavior,
      props.validationContextType
    );

  if (!schemaFactory || !formBuilder || !formValue || coreDataLoading || productsLoading) {
    return (
      <section className="personal-account-acceptable-use-form">
        <LoadingV2 />
      </section>
    );
  }

  return (
    <section className="personal-account-acceptable-use-form">
      <div className="personal-account-acceptable-use-form">
        <FieldFormV2<FinancialApplication>
          ref={fieldFormModule}
          value={formValue}
          fieldSchemaFormBuilder={formBuilder}
          onFormSubmit={async (value) => {
            await saveFormData(value);
          }}
          onFieldChanged={(newValue, newFieldValue) => {
            setFormValue(newValue, newFieldValue);
          }}
          errors={validationErrors}
        >
          <FieldV2<PersonalAccountAcceptableUseComplexValue>
            fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_ACCEPTABLE_USE}
            complexFieldValue={{
              showHeader: false,
              selectedPersonalProducts: personalProducts,
            }}
          />
        </FieldFormV2>
      </div>
    </section>
  );
};
